import { createRouter, createWebHistory } from 'vue-router';
import type { Role } from '@/types';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    roles?: Role[];
    public?: boolean;
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/connection-error',
      component: () => import('@/views/ConnectionErrorView.vue'),
      meta: { public: true, title: '连接重试' },
    },
    {
      path: '/login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true, title: '登录' },
    },
    {
      path: '/',
      component: () => import('@/layouts/AppShell.vue'),
      children: [
        {
          path: 'employee-benefits',
          component: () => import('@/views/EmployeeBenefitsView.vue'),
          meta: { title: '员工福利与奖金', roles: ['OWNER', 'FINANCE', 'ADMIN'] },
        },
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/DashboardView.vue'),
          meta: { title: '工作台' },
        },
        {
          path: 'expenses',
          name: 'expenses',
          component: () => import('@/views/ExpensesView.vue'),
          meta: {
            title: '开销记录',
            roles: ['EMPLOYEE', 'PROCUREMENT', 'OWNER', 'FINANCE', 'ADMIN'],
          },
        },
        {
          path: 'reimbursements',
          name: 'reimbursements',
          component: () => import('@/views/ReimbursementsView.vue'),
          meta: {
            title: '报销单',
            roles: ['EMPLOYEE', 'PROCUREMENT', 'OWNER', 'FINANCE', 'ADMIN'],
          },
        },
        {
          path: 'attendance-requests',
          name: 'attendance-requests',
          component: () => import('@/views/AttendanceRequestsView.vue'),
          meta: {
            title: '员工考勤',
            roles: ['EMPLOYEE', 'PROCUREMENT', 'OWNER', 'FINANCE', 'ADMIN'],
          },
        },
        {
          path: 'performance-requests',
          name: 'performance-requests',
          component: () => import('@/views/PerformanceRequestsView.vue'),
          meta: {
            title: '员工绩效',
            roles: ['EMPLOYEE', 'PROCUREMENT', 'OWNER', 'FINANCE', 'ADMIN'],
          },
        },
        {
          path: 'fund-requests',
          name: 'fund-requests',
          component: () => import('@/views/FundRequestsView.vue'),
          meta: { title: '资金申请', roles: ['PROCUREMENT', 'OWNER', 'FINANCE', 'ADMIN'] },
        },
        {
          path: 'payroll',
          name: 'payroll',
          component: () => import('@/views/PayrollView.vue'),
          meta: { title: '工资表申请', roles: ['FINANCE', 'OWNER', 'ADMIN'] },
        },
        {
          path: 'suppliers',
          name: 'suppliers',
          component: () => import('@/views/SuppliersView.vue'),
          meta: { title: '供应商资料', roles: ['PROCUREMENT', 'OWNER', 'FINANCE', 'ADMIN'] },
        },
        {
          path: 'approvals',
          name: 'approvals',
          component: () => import('@/views/ApprovalsView.vue'),
          meta: { title: '待我审批', roles: ['OWNER'] },
        },
        {
          path: 'payments',
          name: 'payments',
          component: () => import('@/views/PaymentsView.vue'),
          meta: { title: '财务打款', roles: ['FINANCE'] },
        },
        {
          path: 'users',
          name: 'users',
          component: () => import('@/views/UsersView.vue'),
          meta: { title: '人员管理', roles: ['ADMIN'] },
        },
        {
          path: 'hospitals',
          name: 'hospitals',
          component: () => import('@/views/HospitalsView.vue'),
          meta: { title: '医院资料', roles: ['OWNER', 'ADMIN'] },
        },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

router.beforeEach(async (to) => {
  const { useAuthStore } = await import('@/stores/auth');
  const auth = useAuthStore();
  document.title = `${to.meta.title ?? '公司内部账目系统'} | 公司内部账目系统`;
  if (to.path === '/connection-error') return true;
  if (!auth.initialized) {
    try {
      await auth.fetchMe();
    } catch {
      return { path: '/connection-error', query: { redirect: to.fullPath }, replace: true };
    }
  }
  if (to.meta.public) return auth.user ? '/' : true;
  if (!auth.user) return { path: '/login', query: { redirect: to.fullPath } };
  if (to.meta.roles && !to.meta.roles.includes(auth.user.role)) return '/';
  return true;
});

export default router;
